import { useGoogleMap } from '@react-google-maps/api';
import { useEffect, useRef } from 'react';

/**
 * Erweiterte Marker-Komponente, die den neuen google.maps.marker.AdvancedMarkerElement verwendet
 * Falls nicht verfügbar, wird auf den alten Marker zurückgegriffen
 * 
 * @param {Object} props - Die Komponenteneigenschaften
 * @param {Object} props.position - Position des Markers { lat, lng }
 * @param {string} props.title - Titel des Markers (beim Hover angezeigt)
 * @param {Object} props.icon - Icon-Konfiguration { url, scaledSize }
 * @param {string} props.animation - Animation für den Marker
 * @param {function} props.onClick - Callback-Funktion für Klick-Event
 * @param {number} props.zIndex - Z-Index des Markers
 */
function AdvancedMarker({ 
  position, 
  title, 
  icon, 
  animation, 
  onClick,
  zIndex
}) {
  const mapInstance = useGoogleMap();
  const markerRef = useRef(null);
  const retryCountRef = useRef(0);

  useEffect(() => {
    if (!mapInstance || !window.google) return;

    // Funktion zum Erstellen des Markers mit Verzögerung für späteren Versuch
    const createMarker = async () => {
      // Erweiterte Prüfung der API-Verfügbarkeit
      const isAdvancedMarkerAvailable = 
        window.google.maps.marker && 
        typeof window.google.maps.marker.AdvancedMarkerElement === 'function';
      
      // Debug-Info zum Überprüfen der Verfügbarkeit
      console.log("AdvancedMarkerElement verfügbar:", isAdvancedMarkerAvailable, "Versuch:", retryCountRef.current + 1);
      
      if (isAdvancedMarkerAvailable) {
        // Bestehenden Marker entfernen falls vorhanden
        if (markerRef.current) {
          markerRef.current.map = null;
        }

        // Marker-Optionen
        const options = {
          position,
          title,
          map: mapInstance
        };

        // Icon-Optionen, falls vorhanden
        if (icon) {
          // Pin-Optionen für AdvancedMarkerElement
          const pin = new window.google.maps.marker.PinElement({
            scale: icon.scaledSize ? (icon.scaledSize.width / 32) : 1,
            background: icon.url ? 
              icon.url.includes('green') ? '#00C853' : 
              icon.url.includes('blue') ? '#1E88E5' : 
              icon.url.includes('red') ? '#D32F2F' : 
              icon.url.includes('gray') ? '#757575' : '#FFC107'
              : '#FFC107'
          });
          options.content = pin.element;
        }

        // Neuen Marker erstellen
        const marker = new window.google.maps.marker.AdvancedMarkerElement(options);
        
        // Event-Listener, falls vorhanden
        if (onClick) {
          // Bei AdvancedMarkerElement 'gmp-click' statt 'click' verwenden
          marker.addListener('gmp-click', onClick);
        }

        markerRef.current = marker;
      } else {
        // Bei ersten beiden Versuchen: Wenn der AdvancedMarkerElement nicht verfügbar ist, versuche es mit Verzögerung erneut
        if (retryCountRef.current < 2) {
          console.log(`Warte auf AdvancedMarkerElement, Versuch ${retryCountRef.current + 1}/3...`);
          retryCountRef.current += 1;
          // Nach kurzer Verzögerung erneut versuchen (500ms)
          setTimeout(() => createMarker(), 500);
          return;
        }
        
        // Nach 3 Versuchen oder wenn AdvancedMarkerElement nicht verfügbar ist, auf regulären Marker zurückfallen
        console.warn('AdvancedMarkerElement nicht verfügbar nach 3 Versuchen, verwende regulären Marker');
        console.log('Google Maps API Version:', window.google.maps.version);
        
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }

        const marker = new window.google.maps.Marker({
          position,
          title,
          icon,
          animation,
          map: mapInstance,
          zIndex
        });

        if (onClick) {
          marker.addListener('click', onClick);
        }

        markerRef.current = marker;
      }
    };

    // Retry-Count beim erneuten Mounten zurücksetzen
    retryCountRef.current = 0;
    
    // Initial Marker erstellen
    createMarker();

    return () => {
      if (markerRef.current) {
        if (markerRef.current.setMap) {
          markerRef.current.setMap(null);
        } else {
          markerRef.current.map = null;
        }
        markerRef.current = null;
      }
    };
  }, [mapInstance, position, title, icon, animation, onClick, zIndex]);

  return null;
}

export default AdvancedMarker;